"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { PassengersIcon } from "@/app/assets/icons";
import { ScrollableSliderProps } from "@/app/components/ScrollableSlider";
import { getStrapiMedia } from "@/app/utils/api-helpers";

export const ScrollableSliderMobile = ({ data }: ScrollableSliderProps) => {
  const slides = useMemo(() => data?.slide ?? [], [data?.slide]);
  const slidesCount = slides.length;
  const hasLoop = slidesCount > 1;
  const [displayIndex, setDisplayIndex] = useState(hasLoop ? 1 : 0);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ isDragging: boolean; startX: number }>({
    isDragging: false,
    startX: 0,
  });
  // Ref for instantaneous guard (state batch may delay)
  const transitioningRef = useRef(false);
  const extendedSlides = useMemo(() => {
    if (!hasLoop || slidesCount === 0) {
      return slides;
    }
    const firstSlide = slides[0];
    const lastSlide = slides[slidesCount - 1];
    return [lastSlide, ...slides, firstSlide];
  }, [hasLoop, slides, slidesCount]);
  const activeDotIndex = hasLoop
    ? ((displayIndex - 1 + slidesCount) % slidesCount + slidesCount) %
      slidesCount
    : displayIndex;

  useEffect(() => {
    setDisplayIndex(hasLoop ? 1 : 0);
    setDragOffset(0);
  }, [hasLoop]);

  useEffect(() => {
    if (shouldAnimate) return;
    const id = requestAnimationFrame(() => setShouldAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [shouldAnimate]);

  const goToSlide = useCallback(
    (direction: "next" | "prev") => {
      if (!slidesCount) return;
      // Guard against interaction mid-transition or drag via ref (avoids stale state)
      if (transitioningRef.current || dragStateRef.current.isDragging) return;

      setDragOffset(0);

      if (!hasLoop || slidesCount <= 1) {
        setDisplayIndex((prev) => {
          if (direction === "next") return (prev + 1) % slidesCount;
          return (prev - 1 + slidesCount) % slidesCount;
        });
        return;
      }

      setShouldAnimate(true);
      transitioningRef.current = true;
      setIsTransitioning(true);
      setDisplayIndex((prev) => prev + (direction === "next" ? 1 : -1));
    },
    [hasLoop, slidesCount]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!hasLoop || slidesCount <= 1) return;
      if (transitioningRef.current) return; // Block starting drag while animating

      dragStateRef.current = { isDragging: true, startX: event.clientX };
      setIsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [hasLoop, slidesCount]
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    setDragOffset(event.clientX - dragStateRef.current.startX);
  }, []);

  const finishDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStateRef.current.isDragging) return;

      const delta = event.clientX - dragStateRef.current.startX;
      dragStateRef.current = { isDragging: false, startX: 0 };

      if (!sliderRef.current) {
        setDragOffset(0);
        setIsDragging(false);
        return;
      }

      const threshold = sliderRef.current.clientWidth * 0.15;

      if (delta > threshold) {
        goToSlide("prev");
      } else if (delta < -threshold) {
        goToSlide("next");
      } else {
        setDragOffset(0);
      }

      setIsDragging(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    },
    [goToSlide]
  );

  const cancelDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current.isDragging) {
      return;
    }

    dragStateRef.current = { isDragging: false, startX: 0 };
    setDragOffset(0);
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  const ArrowIcon = ({ direction }: { direction: "next" | "prev" }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={direction === "prev" ? "rotate-180" : ""}
    >
      <path
        d="M6 3L10 8L6 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const handleTransitionEnd = useCallback(() => {
    if (!hasLoop || slidesCount <= 1) {
      transitioningRef.current = false;
      setIsTransitioning(false);
      return;
    }

    if (displayIndex === 0) {
      // Jump to last real slide
      setShouldAnimate(false);
      setDisplayIndex(slidesCount);
    } else if (displayIndex === slidesCount + 1) {
      // Jump to first real slide
      setShouldAnimate(false);
      setDisplayIndex(1);
    }
    transitioningRef.current = false;
    setIsTransitioning(false);
  }, [displayIndex, hasLoop, slidesCount]);

  // Safety clamp in case of unexpected rapid state updates
  useEffect(() => {
    if (!hasLoop) return;
    const maxIndex = slidesCount + 1;
    if (displayIndex < 0) {
      setDisplayIndex(0);
    } else if (displayIndex > maxIndex) {
      setDisplayIndex(maxIndex);
    }
  }, [displayIndex, hasLoop, slidesCount]);

  const visibleSlides = extendedSlides;
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sliderWidth = containerWidth || 0;
  const allowTransition = !isDragging && shouldAnimate;
  const translatePx = (-displayIndex * sliderWidth) + (isDragging ? dragOffset : 0);

  if (!slides.length) {
    return null;
  }

  return (
    <div
      ref={sliderRef}
      className="relative w-full overflow-hidden pb-[48px]"
      style={{ touchAction: "pan-y" }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Vehicle options slider"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishDrag}
      onPointerLeave={(event) => {
        if (dragStateRef.current.isDragging) {
          finishDrag(event);
        }
      }}
      onPointerCancel={cancelDrag}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          goToSlide("next");
        } else if (e.key === "ArrowLeft") {
          goToSlide("prev");
        }
      }}
      tabIndex={0}
    >
      <div
        className={`flex ${
          allowTransition ? "transition-transform duration-500" : ""
        }`}
        style={{
          transform: `translate3d(${translatePx}px,0,0)`,
          willChange: "transform",
        }}
        onTransitionEnd={handleTransitionEnd}
        aria-live="polite"
      >
        {visibleSlides.map((item, index) => {
          const imageUrl =
            getStrapiMedia(item.image.data?.attributes.url ?? "") ?? "";

          return (
            <div
              key={`${item?.id ?? index}-${index}`}
              className="w-full flex-shrink-0 p-[16px] bg-[#fff]"
            >
              <h2 className="font-bold text-2xl text-center uppercase">
                {item?.textA}
              </h2>
              <div className="flex items-center justify-center mb-[30px]">
                <PassengersIcon className="min-w-[24px]" />
                <span className="text-[16px] leading-[16px] ml-[5px]">
                  {item?.textB}
                </span>
              </div>
              <div className="flex items-center justify-center my-2">
                <Image
                  src={imageUrl}
                  alt="Preview bus"
                  width={344}
                  height={240}
                  className="object-cover w-full h-auto"
                />
              </div>
              <ul className="py-[16px] text-center">
                {item?.description
                  ?.split("\n")
                  .filter(Boolean)
                  .map((line, index) => (
                    <li
                      key={`${item?.id}-${index}`}
                      className="font-bold text-[16px] leading-[24px] uppercase tracking-[0.08em] list-disc list-inside"
                    >
                      {line}
                    </li>
                  ))}
              </ul>
              <Link
                href="#quote-page"
                className="bg-red text-white px-1/2 dt:px-[24px] py-[12px] dt:mt-1.5 rounded-[8px] uppercase font-bold text-[16px] leading-[16px] dt:leading-[24px] tracking-wider block mx-auto w-fit"
              >
                GET A QUOTE
              </Link>
            </div>
          );
        })}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            className="absolute left-[10px] top-1/2 z-10 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-full border border-[#D7D7D7] bg-white text-black shadow-sm transition hover:border-red hover:text-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red"
            onClick={() => goToSlide("prev")}
            disabled={isTransitioning || isDragging}
          >
            <ArrowIcon direction="prev" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className="absolute right-[10px] top-1/2 z-10 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-full border border-[#D7D7D7] bg-white text-black shadow-sm transition hover:border-red hover:text-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red"
            onClick={() => goToSlide("next")}
            disabled={isTransitioning || isDragging}
          >
            <ArrowIcon direction="next" />
          </button>

          <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {slides.map((slide, index) => {
              const isActive = index === activeDotIndex;
              return (
                <button
                  key={slide?.id ?? index}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`h-[6px] w-[6px] rounded-full transition ${
                    isActive ? "bg-red scale-110" : "bg-gray-300 opacity-70"
                  }`}
                  disabled={isTransitioning || isDragging}
                  onClick={() => {
                    if (transitioningRef.current) return;
                    if (hasLoop) {
                      setShouldAnimate(true);
                      transitioningRef.current = true;
                      setIsTransitioning(true);
                      setDisplayIndex(index + 1);
                    } else {
                      setDisplayIndex(index);
                    }
                    setDragOffset(0);
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
